/**
 * B1: Node Palette — Sprint 17 enhanced with Lucide icons + multi-channel nodes
 * Click to add a node to the journey canvas.
 */
import { useTranslation } from "@/i18n";
import type { LucideIcon } from "lucide-react";
import {
  Zap, MessageSquare, Clock, GitBranch, Target,
  Smartphone, MessageCircle, Send, Split, Webhook,
} from "lucide-react";
import type { JourneyNodeType } from "./types";

interface PaletteItem {
  type: JourneyNodeType;
  Icon: LucideIcon;
  labelKey: string;
  color: string;
}

const PALETTE_ITEMS: PaletteItem[] = [
  { type: "trigger", Icon: Zap, labelKey: "journey.node.trigger", color: "bg-[var(--color-success)]/10 border-[var(--color-success)]/30 text-[var(--color-success)]" },
  { type: "message", Icon: MessageSquare, labelKey: "journey.node.message", color: "bg-[var(--color-primary)]/10 border-[var(--color-primary)]/30 text-[var(--color-primary)]" },
  { type: "sms", Icon: Smartphone, labelKey: "journey.node.sms", color: "bg-[var(--color-info)]/10 border-[var(--color-info)]/30 text-[var(--color-info)]" },
  { type: "whatsapp", Icon: MessageCircle, labelKey: "journey.node.whatsapp", color: "bg-[var(--color-success)]/10 border-[var(--color-success)]/30 text-[var(--color-success)]" },
  { type: "dm", Icon: Send, labelKey: "journey.node.dm", color: "bg-[var(--color-ai)]/10 border-[var(--color-ai)]/30 text-[var(--color-ai)]" },
  { type: "wait", Icon: Clock, labelKey: "journey.node.wait", color: "bg-[var(--color-warning)]/10 border-[var(--color-warning)]/30 text-[var(--color-warning)]" },
  { type: "condition", Icon: GitBranch, labelKey: "journey.node.condition", color: "bg-[var(--color-ai)]/10 border-[var(--color-ai)]/30 text-[var(--color-ai)]" },
  { type: "split", Icon: Split, labelKey: "journey.node.split", color: "bg-[var(--color-accent-action)]/10 border-[var(--color-accent-action)]/30 text-[var(--color-accent-action)]" },
  { type: "webhook", Icon: Webhook, labelKey: "journey.node.webhook", color: "bg-[var(--color-muted-foreground)]/10 border-[var(--color-muted-foreground)]/30 text-[var(--color-muted-foreground)]" },
  { type: "goal", Icon: Target, labelKey: "journey.node.goal", color: "bg-[var(--color-success)]/10 border-[var(--color-success)]/30 text-[var(--color-success)]" },
];

interface NodePaletteProps {
  onAddNode: (type: JourneyNodeType) => void;
  /** Optional filter to show only specific node types (e.g., DM flow builder shows DM-only nodes) */
  allowedTypes?: JourneyNodeType[];
}

export function NodePalette({ onAddNode, allowedTypes }: NodePaletteProps) {
  const { t } = useTranslation();
  const items = allowedTypes
    ? PALETTE_ITEMS.filter((item) => allowedTypes.includes(item.type))
    : PALETTE_ITEMS;

  return (
    <div className="flex flex-col gap-2 p-3 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)]">
      <div className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide">
        {t("journey.palette.title")}
      </div>
      {items.map((item) => (
        <button
          key={item.type}
          type="button"
          onClick={() => onAddNode(item.type)}
          className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium hover:shadow-sm transition-shadow ${item.color}`}
        >
          <item.Icon className="h-4 w-4" />
          <span>{t(item.labelKey)}</span>
        </button>
      ))}
    </div>
  );
}
